import { aiCompanyValuation, companySharePrice, maxAiShareIssue, PLAYER_COMPANY_ID, updateAiCompanySharePrice } from './listing';
import type { DividendPeriod, GameState, VentureRound } from './types';

const QUARTER_WEEKS = 13;
const YEAR_WEEKS = 52;
const MAX_PAYOUT_RATIO = 0.5;

const clone = <T,>(value: T): T => structuredClone(value);

function periodWeeks(period: DividendPeriod): number {
  return period === 'quarterly' ? QUARTER_WEEKS : YEAR_WEEKS;
}

function periodKey(state: GameState, period: DividendPeriod): string {
  return `${period}-${Math.floor(state.week / periodWeeks(period))}`;
}

function periodProfit(state: GameState, period: DividendPeriod): number {
  const weeks = periodWeeks(period);
  const end = Math.floor(state.week / weeks) * weeks;
  const start = end - weeks + 1;
  return Math.max(0, state.reports
    .filter((report) => report.week >= start && report.week <= end)
    .reduce((sum, report) => sum + (report.netProfit ?? 0), 0));
}

function addDividendLedger(state: GameState, companyName: string, amount: number): void {
  state.ledger.push({
    id: `ledger-${state.nextId++}`,
    week: state.week,
    category: 'dividend',
    cashAmount: amount,
    profitAmount: 0,
    label: `${companyName} 分红到账`,
  });
}

function repricePlayerListing(state: GameState): void {
  const listing = state.companyListing;
  if (!listing.listed || listing.totalShares <= 0) return;
  const price = listing.lastValuation / listing.totalShares;
  state.companyListing = {
    ...listing,
    sharePrice: price,
    sharePriceHistory: [...(listing.sharePriceHistory ?? []), { week: state.week, price }].slice(-104),
  };
}

function nextVentureId(state: GameState): string {
  return `venture-${state.nextId++}`;
}

function hasOpenRound(state: GameState, companyId: string): boolean {
  return (state.ventureRounds ?? []).some((round) => round.companyId === companyId && round.status === 'open');
}

function ensurePositiveAmount(value: number, label: string): number {
  const amount = Math.floor(Number(value));
  if (!Number.isFinite(amount) || amount <= 0) throw new Error(`${label}必须大于 0`);
  return amount;
}

export function declarePlayerDividend(state: GameState, period: DividendPeriod, requestedRatio: number): GameState {
  const next = clone(state);
  const listing = next.companyListing;
  const weeks = periodWeeks(period);
  if (!listing.listed || listing.totalShares <= 0) throw new Error('公司上市后才可以分红');
  if (next.week < weeks) throw new Error(`至少经营满 ${weeks} 周后才可以发放${period === 'quarterly' ? '季度' : '年度'}分红`);
  const key = periodKey(next, period);
  if ((next.dividendRecords ?? []).some((record) => record.companyId === PLAYER_COMPANY_ID && record.periodKey === key)) {
    throw new Error(`本${period === 'quarterly' ? '季度' : '年度'}已经分红`);
  }
  const ratio = Number(requestedRatio);
  if (!Number.isFinite(ratio) || ratio <= 0 || ratio > MAX_PAYOUT_RATIO) throw new Error('分红比例必须在 0% 到 50% 之间');
  const profitBase = periodProfit(next, period);
  if (profitBase <= 0) throw new Error('对应期间没有可分配的正净利润');
  const totalDividend = Math.min(Math.floor(profitBase * ratio), Math.max(0, next.cash));
  if (totalDividend <= 0) throw new Error('公司现金不足，无法发放分红');
  const perShare = totalDividend / listing.totalShares;
  const playerDividend = perShare * Math.max(0, next.playerAssets.shares);
  next.cash -= totalDividend;
  next.playerAssets = { ...next.playerAssets, cash: next.playerAssets.cash + playerDividend };
  next.dividendRecords = [
    ...(next.dividendRecords ?? []),
    {
      id: `dividend-${next.nextId++}`,
      companyId: PLAYER_COMPANY_ID,
      companyName: next.companyName,
      period,
      periodKey: key,
      week: next.week,
      profitBase,
      payoutRatio: ratio,
      totalDividend,
      playerDividend,
      perShare,
    },
  ].slice(-104);
  addDividendLedger(next, next.companyName, playerDividend);
  return next;
}

export function startPlayerVentureRound(state: GameState, requestedAmount: number, requestedSecondarySale: number): GameState {
  const next = clone(state);
  const listing = next.companyListing;
  if (!listing.listed || listing.totalShares <= 0) throw new Error('公司上市后才可以向投资公司融资');
  if (hasOpenRound(next, PLAYER_COMPANY_ID)) throw new Error('当前已经有一笔风投融资进行中');
  const targetAmount = ensurePositiveAmount(requestedAmount, '融资金额');
  const price = companySharePrice(next, PLAYER_COMPANY_ID);
  const primaryShares = Math.floor(targetAmount / price);
  if (!Number.isFinite(price) || price <= 0 || primaryShares <= 0) throw new Error('当前股价不足以完成定向增发');
  const normalizedTarget = Math.round(primaryShares * price);
  const maxSecondary = Math.min(normalizedTarget / 5, next.playerAssets.shares * price);
  const secondarySaleAmount = Math.min(Math.max(0, Number(requestedSecondarySale) || 0), maxSecondary);
  const playerSecondaryShares = Math.floor(secondarySaleAmount / price);
  const actualSecondarySaleAmount = Math.round(playerSecondaryShares * price);
  next.cash += normalizedTarget;
  next.playerAssets = {
    ...next.playerAssets,
    cash: next.playerAssets.cash + actualSecondarySaleAmount,
    shares: next.playerAssets.shares - playerSecondaryShares,
  };
  next.companyListing = {
    ...listing,
    totalShares: listing.totalShares + primaryShares,
    institutionalShares: (listing.institutionalShares ?? 0) + primaryShares,
  };
  repricePlayerListing(next);
  const round: VentureRound = {
    id: nextVentureId(next),
    companyId: PLAYER_COMPANY_ID,
    companyName: next.companyName,
    kind: 'player',
    openedWeek: next.week,
    closeWeek: next.week,
    targetAmount: normalizedTarget,
    pricePerShare: price,
    primaryShares,
    playerInvestment: 0,
    playerShares: 0,
    systemInvestment: normalizedTarget,
    systemShares: primaryShares,
    secondarySaleAmount: actualSecondarySaleAmount,
    playerSecondaryShares,
    status: 'closed',
    finalizedWeek: next.week,
  };
  next.ventureRounds = [...(next.ventureRounds ?? []), round].slice(-52);
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'stock_issue',
    cashAmount: normalizedTarget,
    profitAmount: 0,
    objectId: round.id,
    label: `风投定向增发 ${primaryShares.toLocaleString('zh-CN')} 股`,
  });
  if (actualSecondarySaleAmount > 0) {
    next.ledger.push({
      id: `ledger-${next.nextId++}`,
      week: next.week,
      category: 'player_stock_sell',
      cashAmount: actualSecondarySaleAmount,
      profitAmount: 0,
      objectId: round.id,
      label: `原始股份随风投转让 ${playerSecondaryShares.toLocaleString('zh-CN')} 股`,
    });
  }
  return next;
}

export function startAiVentureRound(state: GameState, companyId: string, requestedAmount: number): GameState {
  const next = clone(state);
  const manager = next.aiManagers.find((item) => item.id === companyId);
  const listing = manager?.companyListing;
  if (!manager || manager.bankrupt || !listing?.listed || listing.totalShares <= 0) throw new Error('该 AI 公司当前不能发起路演');
  if (hasOpenRound(next, companyId)) throw new Error('该公司已经有一笔路演进行中');
  const price = companySharePrice(next, companyId);
  const maxShares = maxAiShareIssue(manager);
  const requested = ensurePositiveAmount(requestedAmount, '路演融资金额');
  const primaryShares = Math.min(maxShares, Math.floor(requested / price));
  if (!Number.isFinite(price) || price <= 0 || primaryShares <= 0) throw new Error('当前 AI 公司没有足够的增发空间');
  const targetAmount = Math.round(primaryShares * price);
  const round: VentureRound = {
    id: nextVentureId(next),
    companyId,
    companyName: manager.name,
    kind: 'ai',
    openedWeek: next.week,
    closeWeek: next.week + 1,
    targetAmount,
    pricePerShare: price,
    primaryShares,
    playerInvestment: 0,
    playerShares: 0,
    systemInvestment: 0,
    systemShares: 0,
    secondarySaleAmount: 0,
    playerSecondaryShares: 0,
    status: 'open',
  };
  next.ventureRounds = [...(next.ventureRounds ?? []), round].slice(-52);
  return next;
}

export function investInVentureRound(state: GameState, roundId: string, requestedAmount: number): GameState {
  const next = clone(state);
  const round = next.ventureRounds?.find((item) => item.id === roundId && item.status === 'open');
  if (!round) throw new Error('路演不存在或已结束');
  if (next.week >= round.closeWeek) throw new Error('本轮路演已经截止');
  const remaining = round.targetAmount - round.playerInvestment;
  const amount = Math.min(ensurePositiveAmount(requestedAmount, '投资金额'), remaining);
  const shares = Math.floor(amount / round.pricePerShare);
  const actualAmount = Math.round(shares * round.pricePerShare);
  if (shares <= 0 || actualAmount <= 0) throw new Error('投资金额不足以购买一股');
  if (next.playerAssets.cash < actualAmount) throw new Error('个人现金不足');
  next.playerAssets = {
    ...next.playerAssets,
    cash: next.playerAssets.cash - actualAmount,
    stockPortfolio: {
      ...(next.playerAssets.stockPortfolio ?? {}),
      [round.companyId]: (next.playerAssets.stockPortfolio?.[round.companyId] ?? 0) + shares,
    },
    stockPortfolioCost: {
      ...(next.playerAssets.stockPortfolioCost ?? {}),
      [round.companyId]: (next.playerAssets.stockPortfolioCost?.[round.companyId] ?? 0) + actualAmount,
    },
  };
  round.playerInvestment += actualAmount;
  round.playerShares += shares;
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'player_stock_buy',
    cashAmount: -actualAmount,
    profitAmount: 0,
    objectId: round.companyId,
    label: `参与 ${round.companyName} AI 路演`,
  });
  return next;
}

export function finalizeVentureRounds(state: GameState): GameState {
  const next = clone(state);
  for (const round of next.ventureRounds ?? []) {
    if (round.status !== 'open' || round.closeWeek > next.week) continue;
    const manager = next.aiManagers.find((item) => item.id === round.companyId);
    if (!manager || manager.bankrupt || !manager.companyListing?.listed) {
      const portfolio = { ...(next.playerAssets.stockPortfolio ?? {}) };
      const cost = { ...(next.playerAssets.stockPortfolioCost ?? {}) };
      next.playerAssets = {
        ...next.playerAssets,
        cash: next.playerAssets.cash + round.playerInvestment,
        stockPortfolio: { ...portfolio, [round.companyId]: Math.max(0, (portfolio[round.companyId] ?? 0) - round.playerShares) },
        stockPortfolioCost: { ...cost, [round.companyId]: Math.max(0, (cost[round.companyId] ?? 0) - round.playerInvestment) },
      };
      round.status = 'cancelled';
      continue;
    }
    round.systemInvestment = Math.max(0, round.targetAmount - round.playerInvestment);
    round.systemShares = Math.max(0, round.primaryShares - round.playerShares);
    manager.cash += round.targetAmount;
    manager.companyListing = {
      ...manager.companyListing,
      totalShares: manager.companyListing.totalShares + round.primaryShares,
      institutionalShares: (manager.companyListing.institutionalShares ?? 0) + round.primaryShares,
    };
    round.status = 'closed';
    round.finalizedWeek = next.week;
    updateAiCompanySharePrice(manager, next.week);
  }
  return next;
}

function addAutomaticDividend(next: GameState, companyId: string, companyName: string, period: DividendPeriod, profitBase: number, cash: number, totalShares: number, playerShares: number): number {
  const key = periodKey(next, period);
  if ((next.dividendRecords ?? []).some((record) => record.companyId === companyId && record.periodKey === key)) return 0;
  const ratio = period === 'annual' ? 0.3 : 0.2;
  const totalDividend = Math.min(Math.floor(profitBase * ratio), Math.floor(Math.max(0, cash) * 0.2));
  if (totalDividend <= 0 || totalShares <= 0) return 0;
  const playerDividend = totalDividend * Math.max(0, playerShares) / totalShares;
  next.dividendRecords = [
    ...(next.dividendRecords ?? []),
    {
      id: `dividend-${next.nextId++}`,
      companyId,
      companyName,
      period,
      periodKey: key,
      week: next.week,
      profitBase,
      payoutRatio: ratio,
      totalDividend,
      playerDividend,
      perShare: totalDividend / totalShares,
    },
  ].slice(-104);
  next.playerAssets = { ...next.playerAssets, cash: next.playerAssets.cash + playerDividend };
  if (playerDividend > 0) addDividendLedger(next, companyName, playerDividend);
  return totalDividend;
}

export function processAutomaticDividends(state: GameState): GameState {
  const next = clone(state);
  const period: DividendPeriod | null = next.week % YEAR_WEEKS === 0 ? 'annual' : next.week % QUARTER_WEEKS === 0 ? 'quarterly' : null;
  if (!period) return next;
  const weeks = periodWeeks(period);
  for (const manager of next.aiManagers ?? []) {
    if (manager.bankrupt || !manager.companyListing?.listed) continue;
    const profitBase = manager.routes.filter((route) => route.active).reduce((sum, route) => sum + Math.max(0, route.stats?.operatingProfit ?? 0), 0) * weeks;
    const totalDividend = addAutomaticDividend(
      next,
      manager.id,
      manager.name,
      period,
      profitBase,
      manager.cash,
      manager.companyListing.totalShares,
      next.playerAssets.stockPortfolio?.[manager.id] ?? 0,
    );
    manager.cash -= totalDividend;
  }
  return next;
}

export function ventureRoundRemaining(round: VentureRound): number {
  return Math.max(0, round.targetAmount - round.playerInvestment);
}

export function ventureCompanyValuation(state: GameState, companyId: string): number {
  if (companyId === PLAYER_COMPANY_ID) return state.companyListing.lastValuation;
  const manager = state.aiManagers.find((item) => item.id === companyId);
  return manager ? aiCompanyValuation(manager) : 0;
}
